import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Bell, BellOff, Send } from "lucide-react";

/**
 * Push notification toggle.
 *  - GET /push/vapid-public-key on mount → know if push is configured
 *  - On enable: ask Notification permission, subscribe via PushManager, POST /push/subscribe
 *  - On disable: unsubscribe locally and call /push/unsubscribe
 *  - "Enviar teste" button calls POST /push/test
 *
 * Hidden when SW or PushManager are not available (e.g., Safari iOS <16.4 PWA-only).
 */
export default function PushToggle() {
  const [supported, setSupported] = useState(null);
  const [vapidKey, setVapidKey] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");

  useEffect(() => {
    const ok = typeof window !== "undefined"
      && "serviceWorker" in navigator
      && "PushManager" in window
      && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    api.get("/push/vapid-public-key").then(async (r) => {
      if (!r.data?.enabled || !r.data?.public_key) {
        setSupported(false);
        return;
      }
      setVapidKey(r.data.public_key);
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setEnabled(!!sub);
      } catch (e) {
        // Best-effort: subscription check fails when navigator.serviceWorker.ready times out
        // (no SW registered yet) or pushManager is unavailable. Safe to ignore — user can re-enable later.
        console.debug("Push subscription check failed:", e?.message || e);
      }
    }).catch((e) => {
      console.debug("VAPID public_key fetch failed:", e?.message || e);
      setSupported(false);
    });
  }, []);

  if (supported === null) return null;
  if (!supported) {
    return (
      <div className="kw-card p-4 text-xs text-[#737373]" data-testid="push-toggle-unsupported">
        Notificações push indisponíveis neste navegador. Funciona em Chrome, Edge, Firefox e Safari iOS ≥16.4 (apenas como app instalada no ecrã principal).
      </div>
    );
  }

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Permissão de notificações negada.");
        setBusy(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        expirationTime: json.expirationTime,
      });
      setEnabled(true);
      toast.success("Notificações ativadas neste dispositivo.");
    } catch (err) { toast.error(formatApiError(err) || err.message); }
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setEnabled(false);
      toast.success("Notificações desativadas neste dispositivo.");
    } catch (err) { toast.error(formatApiError(err) || err.message); }
    setBusy(false);
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/push/test", {});
      if (r.data?.sent > 0) toast.success(`Notificação enviada (${r.data.sent} dispositivo${r.data.sent === 1 ? "" : "s"}).`);
      else toast.error("Nenhuma notificação entregue. Verifica permissões.");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="kw-card p-5" data-testid="push-toggle">
      <div className="flex items-start gap-3 mb-3">
        {enabled ? <Bell className="w-5 h-5 text-[#22C55E]" /> : <BellOff className="w-5 h-5 text-[#737373]" />}
        <div className="flex-1">
          <div className="kw-overline">Notificações no telemóvel</div>
          <p className="text-xs text-[#A3A3A3] mt-1">
            Recebe avisos da segunda-feira (abertura do reporting), da reunião do teu clube (24h e 2h antes), do fecho do reporting ao domingo às 18h se ainda não tiveres reportado, e novidades importantes da liga. Funciona após instalares a app no ecrã principal.
          </p>
        </div>
      </div>

      {permission === "denied" && (
        <p className="text-xs text-[#FFB020] border border-[#FFB020]/30 bg-[#FFB020]/5 p-3 mb-3" data-testid="push-permission-denied">
          As notificações estão bloqueadas no navegador. Para reativar, abre as definições do site e permite notificações.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {enabled ? (
          <>
            <button onClick={disable} disabled={busy} className="kw-btn-secondary !py-2 inline-flex items-center gap-2" data-testid="push-disable">
              <BellOff className="w-4 h-4" /> Desativar
            </button>
            <button onClick={sendTest} disabled={busy} className="kw-btn-primary !py-2 inline-flex items-center gap-2" data-testid="push-test">
              <Send className="w-4 h-4" /> Enviar teste
            </button>
          </>
        ) : (
          <button onClick={enable} disabled={busy || permission === "denied"} className="kw-btn-primary !py-2 inline-flex items-center gap-2" data-testid="push-enable">
            <Bell className="w-4 h-4" /> Ativar notificações
          </button>
        )}
      </div>
    </div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
